import mediaManagerTemplateDataMapper from 'modules/core/domain/data-mappers/media-manager-template-data-mapper';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import {
  COPY_MEDIA_MANAGER_TEMPLATE_MUTATION,
  CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  MEDIA_MANAGER_PRESETS_QUERY,
  MEDIA_MANAGER_TEMPLATES_QUERY,
  MEDIA_MANAGER_TEMPLATE_QUERY,
  MediaManagerTemplateGql,
  UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
} from 'modules/manager/domain/graphql/media-manager-templates';
import { CopyMediaManagerTemplate, CreateMediaManagerTemplate, UpdateMediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/actions';
import { baseApi } from 'shared/api/base-api';

export const MEDIA_MANAGER_TEMPLATE_TAG = 'MediaManagerTemplate' as const;
export const MEDIA_MANAGER_TEMPLATES_LIST_TAG = { type: MEDIA_MANAGER_TEMPLATE_TAG, id: 'ORG-LIST' };
export const MEDIA_MANAGER_PRESETS_LIST_TAG = { type: MEDIA_MANAGER_TEMPLATE_TAG, id: 'PRESET-LIST' };

function mapTemplates(rows: MediaManagerTemplateGql[] | null | undefined): MediaManagerTemplate[] {
  return (rows ?? []).map(mediaManagerTemplateDataMapper);
}

export const mediaManagerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    mediaManagerTemplates: build.query<MediaManagerTemplate[], { tournamentId: number; sportType?: string }>({
      query: ({ tournamentId, sportType }) => ({
        query: MEDIA_MANAGER_TEMPLATES_QUERY,
        variables: { tournamentId, sportType },
      }),
      transformResponse: (raw: { mediaManagerTemplates?: MediaManagerTemplateGql[] }) =>
        mapTemplates(raw.mediaManagerTemplates),
      providesTags: (result) => [
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        ...(result ?? []).map((template) => ({ type: MEDIA_MANAGER_TEMPLATE_TAG, id: template.id })),
      ],
    }),
    mediaManagerPresets: build.query<MediaManagerTemplate[], { sportType?: string } | void>({
      query: (arg) => ({
        query: MEDIA_MANAGER_PRESETS_QUERY,
        variables: { sportType: arg && 'sportType' in arg ? arg.sportType : undefined },
      }),
      transformResponse: (raw: { mediaManagerPresets?: MediaManagerTemplateGql[] }) =>
        mapTemplates(raw.mediaManagerPresets),
      providesTags: (result) => [
        MEDIA_MANAGER_PRESETS_LIST_TAG,
        ...(result ?? []).map((template) => ({ type: MEDIA_MANAGER_TEMPLATE_TAG, id: template.id })),
      ],
    }),
    mediaManagerTemplate: build.query<MediaManagerTemplate, number>({
      query: (id) => ({
        query: MEDIA_MANAGER_TEMPLATE_QUERY,
        variables: { id },
      }),
      transformResponse: (raw: { mediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.mediaManagerTemplate),
      providesTags: (_result, _error, id) => [{ type: MEDIA_MANAGER_TEMPLATE_TAG, id }],
    }),
    createMediaManagerTemplate: build.mutation<MediaManagerTemplate, CreateMediaManagerTemplate>({
      query: (input) => ({
        query: CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { createMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.createMediaManagerTemplate),
      invalidatesTags: (_result, _error, input) =>
        input.tournamentId == null ? [MEDIA_MANAGER_PRESETS_LIST_TAG] : [MEDIA_MANAGER_TEMPLATES_LIST_TAG],
    }),
    updateMediaManagerTemplate: build.mutation<MediaManagerTemplate, UpdateMediaManagerTemplate>({
      query: (input) => ({
        query: UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { updateMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.updateMediaManagerTemplate),
      invalidatesTags: (_result, _error, input) => [
        { type: MEDIA_MANAGER_TEMPLATE_TAG, id: input.id },
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        MEDIA_MANAGER_PRESETS_LIST_TAG,
      ],
    }),
    deleteMediaManagerTemplate: build.mutation<{ status: string }, number>({
      query: (id) => ({
        query: DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { id },
      }),
      transformResponse: (raw: { deleteMediaManagerTemplate: { status: string } }) => raw.deleteMediaManagerTemplate,
      invalidatesTags: (_result, _error, id) => [
        { type: MEDIA_MANAGER_TEMPLATE_TAG, id },
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        MEDIA_MANAGER_PRESETS_LIST_TAG,
      ],
    }),
    copyMediaManagerTemplate: build.mutation<MediaManagerTemplate, CopyMediaManagerTemplate>({
      query: (input) => ({
        query: COPY_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { copyMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.copyMediaManagerTemplate),
      invalidatesTags: [MEDIA_MANAGER_TEMPLATES_LIST_TAG],
    }),
  }),
  overrideExisting: false,
});

export const {
  useMediaManagerTemplatesQuery,
  useMediaManagerPresetsQuery,
  useCreateMediaManagerTemplateMutation,
  useUpdateMediaManagerTemplateMutation,
  useDeleteMediaManagerTemplateMutation,
  useCopyMediaManagerTemplateMutation,
} = mediaManagerApi;
